import React, { useEffect } from "react";
import { styled } from '@mui/material/styles';
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { updateBreadcrumbs } from "../../actions/actions";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTools,
  faQuestionCircle,
  faCloudDownloadAlt,
  faPlusCircle,
  // faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";
import Button from "@mui/material/Button";
import AboutBanner from "../../../assets/images/storm-coming.jpg";
import hazusIcon from "../../../assets/images/hazus.png";

import handleGA, { gaEvents } from "../../googleAnalytics";

const PREFIX = 'About';

const classes = {
  root: `${PREFIX}-root`,

  // migrating styles to emotion: App.theme styles no longer imported from file using useStyles
  banner: `${PREFIX}-banner`,
  bannerTextBox: `${PREFIX}-bannerTextBox`,
  bannerTextBoxHeader: `${PREFIX}-bannerTextBoxHeader`,
  bannerTextBoxSubHeader: `${PREFIX}-bannerTextBoxSubHeader`,
  bannerTextBoxButton: `${PREFIX}-bannerTextBoxButton`,
  infoIcon: `${PREFIX}-infoIcon`,

  container: `${PREFIX}-container`,
  image: `${PREFIX}-image`,
  item: `${PREFIX}-item`,
  itemImage: `${PREFIX}-itemImage`,
  itemContent: `${PREFIX}-itemContent`,
  itemTitle: `${PREFIX}-itemTitle`,
  itemText: `${PREFIX}-itemText`,
  itemLink: `${PREFIX}-itemLink`,
  link: `${PREFIX}-link`,
  pointer: `${PREFIX}-pointer`
};

const Root = styled('div')((
  {
    theme
  }
) => ({
  [`& .${classes.root}`]: {
    background: "#fff",
    display: "block",
    [theme.breakpoints.down("sm")]: {
      width: "100vw",
    },
  },

  [`& .${classes.banner}`]: {
    position: "relative",
    maxWidth: '100vw'
    },

  [`& .${classes.bannerTextBox}`]: {
    position: "absolute",
    left: "12vw",
    top: "5rem",
    display: "flex",
    flexDirection: "column",
    maxWidth: "20rem",
    background: "#fff",
    borderRadius: "5px",
    padding: "2rem",
    lineHeight: "1.5",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "16rem",
      top: "0",
      right: "0",
      bottom: "0",
      left: "0",
      margin: "2rem auto",
    },
  },

  [`& .${classes.bannerTextBoxHeader}`]: {
    color: "#0078a9",
    fontWeight: "bold",
    fontSize: "1.3rem",
    },

  [`& .${classes.bannerTextBoxSubHeader}`]: {
	margin: "1rem 0 0 0",
    },

  [`& .${classes.bannerTextBoxButton}`]: {
    background: "#0078a9",
    color: "#fff",
    textTransform: "none",
    display: "flex",
    alignSelf: "end",
    marginTop: "1.5em",
    "&:hover": {
      background: "#006c98",
    },
  },

  [`& .${classes.infoIcon}`]: {
    color: '#0078a9',
    fontSize: '0.9rem',
    margin: '0 0.25rem'
    },

  [`& .${classes.container}`]: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    maxWidth: "64rem",
    margin: "3em auto",
    [theme.breakpoints.down("sm")]: {
      margin: "3em 1em",
    },
  },

  [`& .${classes.image}`]: {
    maxHeight: "400px",
    minWidth: "100vw",
    objectFit: "cover",
    objectPosition: "bottom",
  },

  [`& .${classes.item}`]: {
    display: "flex",
    flexDirection: "row",
    margin: "2em 0",
    width: "45%",
    [theme.breakpoints.down("sm")]: {
      margin: "2em 0",
      width: "100%",
    },
  },

  [`& .${classes.itemImage}`]: {
    marginRight: "2rem",
    fontSize: "8em",
    width: "8rem !important",
    objectFit: "cover",
    borderRadius: "50%",
    // color: "#0078a9",
    color: "#005287",
    alignSelf: "start",
    overflow: "visible !important",
    [theme.breakpoints.down("sm")]: {
      height: "6rem",
      width: "6rem",
      maxWidth: "6rem",
      fontSize: "6rem",
    },
  },

  [`& .${classes.itemContent}`]: {
    display: "flex",
    flexDirection: "column",
    lineHeight: "1.5215469613",
    flex: 1,
  },

  [`& .${classes.itemTitle}`]: {
    color: "#37783a",
    fontSize: "1.3rem",
    fontWeight: "bold",
    marginBottom: "0.5em",
  },

  [`& .${classes.itemText}`]: {},

  [`& .${classes.itemLink}`]: {
    color: "#37783a",
  },

  [`& .${classes.link}`]: {
    color: "#005287",
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },

  [`& .${classes.pointer}`]: {
    "&:hover": {
      cursor: "pointer",
    },
  }
}));

const createBreadcrumbs = (name) => {
  return [
    { name: "Hazus Loss Library", link: "/library" },
    { name: "Use Cases", link: "/usecases" },
    { name: name, link: "" },
  ];
};

const About = ({ updateBreadcrumbs }) => {

  const history = useHistory();

  const showContribution = false;

  const navLibrary = () => {
    history.push("/library");
  };
  const navUseCases = () => {
    history.push("/usecases");
  };
  const navHelp = () => {
    history.push("/help");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Google Analytics
    handleGA(gaEvents.PAGE_VIEW, window.location.pathname);
  }, []);

  useEffect(() => {
    document.title = "Natural Hazard Risk Information Repository | Hazus Loss Library";
    document.getElementById('description').content = "Digital library offering natural hazard risk info, including historic analyses & predictive data that can enhance mitigation, planning, response, and recovery.";
  })

  return (
    <Root className={classes.root}>
      <div className={classes.banner}>
        <div className={classes.bannerTextBox}>
          <span className={classes.bannerTextBoxHeader}>
            Explore the Hazus Loss Library
          </span>
          <p>
            Find Hazus-generated risk assessments and loss modeling results for
            historic events and planning scenarios for communities throughout
            the United States.
          </p>
          <Button
            variant="contained"
            className={classes.bannerTextBoxButton}
            onClick={navLibrary}
          >
            Explore the Library
          </Button>
        </div>
        <img
          src={AboutBanner}
          alt="a storm coming"
          className={classes.image}
        ></img>
      </div>
      <div className={classes.container}>
        <div className={classes.item}>
          <img
            className={classes.itemImage}
            src={hazusIcon}
            alt="Hazus logo"
          />
          <div className={classes.itemContent}>
            <span className={classes.itemTitle}>
              What is the Hazus Loss Library?
            </span>
            <span className={classes.itemText}>
              The Hazus Loss Library is a centralized repository for accessing
              natural hazard risk information, curated by FEMA's Natural Hazards
              Risk Assessment Program (NHRAP). The goal is to make quantifiable
              risk information accessible nationwide to improve mitigation
              strategies, response efforts, and to expedite recovery.
            </span>
          </div>
        </div>
        <div className={classes.item}>
          <FontAwesomeIcon
            className={classes.itemImage}
            icon={faTools}
          />
          <div className={classes.itemContent}>
            <div
              className={`${classes.itemTitle} ${classes.pointer}`}
              onClick={navUseCases}
            >
              How to use the Hazus Loss Library
            </div>
            <span className={classes.itemText}>
              Search for a historic event, planning scenario, or location, such
              as a state, county, or jurisdiction, and view previously performed
              analyses and downloadable products. Review the <a href="https://www.fema.gov/sites/default/files/documents/fema_hll_how_to_guide_final.pdf" target="_blank" aria-label="HLL How to Guide" rel="noreferrer" className={classes.link}>HLL How to Guide</a> for 
              step-by-step instructions on how to navigate the HLL application to 
              find results for your area of interest and how to download and review 
              available risk information. View the{" "}
              <Link className={classes.link} to="/usecases">
                Use Cases
              </Link>{" "}
              to learn how the Hazus Loss Library can be used.
            </span>
          </div>
        </div>
        <div className={classes.item}>
          <FontAwesomeIcon
            className={classes.itemImage}
            icon={faQuestionCircle}
          />
          <div className={classes.itemContent}>
            <div
              className={`${classes.itemTitle} ${classes.pointer}`}
              onClick={navHelp}
            >
              Get help with using the Hazus Loss Library
            </div>
            <span className={classes.itemText}>
              The Hazus Team is available to answer questions and provide
              support. Reach out to the team at{" "}
              <span className={classes.link}>
                <a href="mailto:fema-hazus-support@fema.dhs.gov">fema-hazus-support@fema.dhs.gov</a>
              </span>{" "}
              or view our{" "}
              <Link className={classes.link} to="/help">
                Help page
              </Link>
              .
            </span>
          </div>
        </div>
        <div className={classes.item}>
          <FontAwesomeIcon
            className={classes.itemImage}
            icon={faCloudDownloadAlt}
          />
          <div className={classes.itemContent}>
            <div
              onClick={navLibrary}
              className={`${classes.itemTitle} ${classes.pointer}`}
            >
              Get products and data
            </div>
            <span className={classes.itemText}>
              <Link
                to="/library"
                className={classes.link}
                value="library"
              >
                Explore the Hazus Loss Library
              </Link>{" "}
              to find the information to best help your community. You can find
              reports, result spreadsheets, geospatial data, hazard data, and
              Hazus Package Region (HPR) files to inform your own analysis.
            </span>
          </div>
        </div>
        {showContribution ? (
          <div className={classes.item}>
            <FontAwesomeIcon
              className={classes.itemImage}
              icon={faPlusCircle}
            />
            <div className={classes.itemContent}>
              <span className={classes.itemTitle}>
                Contribute data to the Hazus Loss Library
              </span>
              <span className={classes.itemText}>
                Do you perform natural hazard risk analysis? We host curated
                work from all over the country. Reach out to
                FEMA-NHRAP@fema.dhs.gov and request to contribute. We would love
                to hear from you!
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </Root>
  );
};

About.propTypes = {
  updateBreadcrumbs: PropTypes.func,
};

export default connect(null, { updateBreadcrumbs })(About);
